import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { 
  ArrowLeft,
  Search,
  Users,
  UserCheck,
  UserX,
  Shield,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  User,
  Phone,
  MapPin,
  Crown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    email: '',
    full_name: '',
    phone: '',
    address: '',
    role: '',
    status: '',
    is_active: true
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (userData) => {
    setSelectedUser(userData);
    setEditForm({
      email: userData.email,
      full_name: userData.full_name,
      phone: userData.phone || '',
      address: userData.address || '',
      role: userData.role,
      status: userData.status,
      is_active: userData.is_active
    });
    setEditModalOpen(true);
  };

  const openPasswordModal = (userData) => {
    setSelectedUser(userData);
    setNewPassword('');
    setPasswordModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editForm);
      toast.success('Usuário atualizado com sucesso!');
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      const message = error.response?.data?.detail || 'Erro ao atualizar usuário';
      toast.error(message);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      const message = error.response?.data?.detail || 'Erro ao alterar senha';
      toast.error(message);
    }
  };

  const quickStatusUpdate = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { status });
      toast.success(`Usuário ${status === 'approved' ? 'aprovado' : 'rejeitado'} com sucesso!`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      const message = error.response?.data?.detail || 'Erro ao atualizar usuário';
      toast.error(message);
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { 
        is_active: !currentStatus 
      });
      toast.success(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user active status:', error);
      const message = error.response?.data?.detail || 'Erro ao atualizar status';
      toast.error(message);
    }
  };

  const deleteUser = async (userId, userRole) => {
    if (userId === user.id) {
      toast.error('Você não pode excluir sua própria conta');
      return;
    }

    if (window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) {
      try {
        await axios.delete(`${API}/admin/users/${userId}`);
        toast.success('Usuário excluído com sucesso!');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        const message = error.response?.data?.detail || 'Erro ao excluir usuário';
        toast.error(message);
      }
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      approved: 'bg-green-500/20 text-green-300',
      rejected: 'bg-red-500/20 text-red-300',
      pending: 'bg-yellow-500/20 text-yellow-300',
      inactive: 'bg-gray-500/20 text-gray-300'
    };

    const labels = {
      approved: 'Aprovado',
      rejected: 'Rejeitado',
      pending: 'Pendente',
      inactive: 'Inativo'
    };

    return (
      <Badge variant="secondary" className={variants[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const variants = {
      superadmin: 'bg-red-500/20 text-red-300',
      admin: 'bg-purple-500/20 text-purple-300',
      user: 'bg-blue-500/20 text-blue-300'
    };

    const labels = {
      superadmin: 'SuperAdmin',
      admin: 'Admin',
      user: 'Usuário'
    };

    const icons = {
      superadmin: <Crown className="h-3 w-3 mr-1" />,
      admin: <Shield className="h-3 w-3 mr-1" />,
      user: <User className="h-3 w-3 mr-1" />
    };

    return (
      <Badge variant="secondary" className={variants[role]}>
        {icons[role]}
        {labels[role]}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-white">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="text-white border-white/20">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <Users className="mr-3 h-6 w-6" />
              Gerenciar Usuários
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white/5 border-white/20 text-white"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="approved">Aprovado</SelectItem>
                  <SelectItem value="rejected">Rejeitado</SelectItem>
                </SelectContent>
              </Select>

              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40 bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Papel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="superadmin">SuperAdmin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>Usuários ({filteredUsers.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-white text-lg mb-2">Nenhum usuário encontrado</p>
                <p className="text-gray-400">Tente ajustar os filtros de busca</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Usuário</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Papel</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Ativo</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Último Login</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-white font-medium">{u.full_name}</p>
                            <p className="text-gray-400 text-sm">{u.email}</p>
                            {u.phone && <p className="text-gray-500 text-xs">{u.phone}</p>}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(u.status)}
                            {getStatusBadge(u.status)}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {getRoleBadge(u.role)}
                        </td>
                        <td className="py-4 px-4">
                          <Switch
                            checked={u.is_active}
                            onCheckedChange={() => toggleUserActive(u.id, u.is_active)}
                            disabled={u.role === 'superadmin' && user.role !== 'superadmin'}
                          />
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {u.last_login 
                            ? new Date(u.last_login).toLocaleDateString('pt-BR')
                            : 'Nunca'
                          }
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            {/* Quick Actions for Pending Users */}
                            {u.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => quickStatusUpdate(u.id, 'approved')}
                                  className="bg-green-600 hover:bg-green-700"
                                  title="Aprovar usuário"
                                >
                                  <UserCheck className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => quickStatusUpdate(u.id, 'rejected')}
                                  title="Rejeitar usuário"
                                >
                                  <UserX className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            
                            {/* Edit User */}
                            <Button
                              size="sm"
                              onClick={() => openEditModal(u)}
                              className="bg-blue-600 hover:bg-blue-700"
                              title="Editar usuário"
                              disabled={u.role === 'superadmin' && user.role !== 'superadmin'}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            
                            {/* Change Password */}
                            <Button
                              size="sm"
                              onClick={() => openPasswordModal(u)}
                              className="bg-orange-600 hover:bg-orange-700"
                              title="Alterar senha"
                              disabled={u.role === 'superadmin' && user.role !== 'superadmin'}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            
                            {/* Delete User */}
                            {u.id !== user.id && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteUser(u.id, u.role)}
                                title="Excluir usuário"
                                disabled={u.role === 'superadmin' && user.role !== 'superadmin'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="bg-gray-900 border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>Editar Usuário</DialogTitle>
              <DialogDescription>
                Atualize as informações do usuário {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="pl-10 bg-white/5 border-white/20 text-white"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Nome Completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                      className="pl-10 bg-white/5 border-white/20 text-white"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="pl-10 bg-white/5 border-white/20 text-white"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Papel</Label>
                  <Select 
                    value={editForm.role} 
                    onValueChange={(value) => setEditForm({...editForm, role: value})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuário</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {user.role === 'superadmin' && (
                        <SelectItem value="superadmin">SuperAdmin</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Endereço</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    value={editForm.address}
                    onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    className="pl-10 bg-white/5 border-white/20 text-white"
                    placeholder="Endereço completo"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Status</Label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(value) => setEditForm({...editForm, status: value})}
                >
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="approved">Aprovado</SelectItem>
                    <SelectItem value="rejected">Rejeitado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  checked={editForm.is_active}
                  onCheckedChange={(checked) => setEditForm({...editForm, is_active: checked})}
                />
                <Label className="text-white">Usuário ativo</Label>
              </div>
              
              <div className="flex justify-end space-x-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditModalOpen(false)}
                  className="text-white border-white/20"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change Password Modal */}
        <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
          <DialogContent className="bg-gray-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Alterar Senha</DialogTitle>
              <DialogDescription>
                Digite a nova senha para {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Nova Senha *</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 bg-white/5 border-white/20 text-white"
                    placeholder="Digite a nova senha"
                    required
                    minLength={6}
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Mínimo de 6 caracteres
                </p>
              </div>
              
              <div className="flex justify-end space-x-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setPasswordModalOpen(false)}
                  className="text-white border-white/20"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                  Alterar Senha
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminUsers;